import axios from 'utils/axios';
import type {
    ProductSetStatisticsType,
    CatalogDirActionResultType,
    GetProductSetListResultType,
    ProductSetCreateDataType,
    ProductSetFullType,
    ProductSetSaveDataType,
} from '@econtractor/types';

export interface ProductSetListParams {
    page?: number;
    search?: string;
    order?: string;
    onPage?: number;
    categoryId?: number | null;
    categoryIds?: number[];
}

export type ProductSetPayload = ProductSetCreateDataType & ProductSetSaveDataType;

export interface ProductSetCreateResponse {
    setId?: number;
    articleDublicate?: boolean;
}

export const getList = async (getData: ProductSetListParams): Promise<GetProductSetListResultType | null> => {
    try {
        const response = await axios('/product-sets', { params: getData });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getProductSet = async (setId: number | string): Promise<ProductSetFullType | null> => {
    try {
        const response = await axios(`/product-sets/${setId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createProductSet = async (createData: ProductSetPayload): Promise<number | false | ProductSetCreateResponse> => {
    try {
        const response = await axios.post('/product-sets/new', createData);
        if (response?.data?.articleDublicate) return response.data as ProductSetCreateResponse;
        return response?.data?.setId || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const updateProductSet = async (setId: number | string, saveData: ProductSetPayload): Promise<boolean> => {
    try {
        const response = await axios.post(`/product-sets/${setId}`, saveData);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteProductSet = async (setId: number | string): Promise<boolean> => {
    try {
        const response = await axios.delete(`/product-sets/${setId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

const handleDirApiError = (e: unknown): CatalogDirActionResultType | null => {
    if (e && typeof e === 'object' && ('stat' in e || 'errCode' in e)) {
        return e as CatalogDirActionResultType;
    }
    if (e && typeof e === 'object' && 'response' in e) {
        const response = (e as { response?: { data?: CatalogDirActionResultType } }).response;
        if (response?.data) return response.data;
    }
    console.error(e);
    return null;
};

export const getDirUsage = async (): Promise<Record<number, number> | null> => {
    try {
        const response = await axios.get('/product-sets/dirs/category/usage');
        return response.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createDir = async (name: string): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post('/product-sets/dirs/category/create', { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const renameDir = async (dirId: number, name: string): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/product-sets/dirs/category/update/${dirId}`, { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const deleteDir = async (dirId: number): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/product-sets/dirs/category/delete/${dirId}`);
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const getStatistics = async (): Promise<ProductSetStatisticsType> =>
    (await axios.get('/product-sets/statistics')).data;
